import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { HTTPCONSTANTS } from '../http/http-constants';
import { showUnfinished } from '../site-config';

// ---- request/response shapes (mirror PlatexAPI commerce DTOs) ----

export interface ClickRequest {
  registration: string;
  plateId?: number;
  sourceId: number;
  clickKind?: 'OUTBOUND' | 'PHONE_REVEAL' | 'WEBSITE';
  destinationUrl?: string;
  sessionId?: string;
  visitorId?: string; // persistent anonymous visitor id (no account required)
}

export interface ClickResponse {
  clickId: number;
  billingStatus: string;
  plateId: number;
  sourceId: number;
}

export interface LeadRequest {
  registration: string;
  plateId?: number;
  sourceId: number;
  leadKind: 'ENQUIRY_FORM' | 'CALLBACK' | 'MESSAGE' | 'PHONE';
  clickId?: number;
  contactPayload?: string;
  sessionId?: string;
}

export interface DealerDailyMetric {
  sourceId: number;
  metricDate: string;
  clicks: number;
  billableClicks: number;
  lockedFreeClicks: number;
  leads: number;
  excludedClicks: number;
}

export interface DealerTopPlate {
  sourceId: number;
  plateId: number;
  registration: string;
  clicks: number;
  billableClicks: number;
  lastClickAt: string;
}

export interface DealerRecentActivity {
  sourceId: number;
  activityType: string;
  refId: number;
  plateId: number;
  registration: string;
  detail: string;
  billingStatus: string;
  occurredAt: string;
  billingReason?: string; // explainability (clicks); null for leads
}

export interface CommerceInvoice {
  invoiceId: number;
  sourceId: number;
  periodStart: string;
  periodEnd: string;
  status: string;
  subtotalPence: number;
  taxPence: number;
  totalPence: number;
}

/**
 * Talks to the PlatexAPI commerce surface (Doc 51 / 004_commerce.sql).
 * Replaces the legacy RegistrationTracking affiliate path: clicks are now
 * attributed server-side by plate_id + source_id, not registration text.
 */
@Injectable({ providedIn: 'root' })
export class CommerceService {
  private readonly base = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/commerce`;

  // localStorage key for the persistent anonymous visitor id.
  private static readonly VISITOR_ID_KEY = 'pla-visitor-id';

  constructor(private http: HttpClient) {}

  /**
   * Stable anonymous visitor id, persisted in localStorage. Lets an anonymous
   * (logged-out) visitor be attributed and click-locked exactly like a
   * logged-in one — no PlateX account required. Server still falls back to a
   * hashed IP+UA if this is unavailable.
   */
  public getVisitorId(): string {
    try {
      let id = localStorage.getItem(CommerceService.VISITOR_ID_KEY);
      if (!id) {
        id =
          typeof crypto !== 'undefined' && (crypto as any).randomUUID
            ? (crypto as any).randomUUID()
            : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(CommerceService.VISITOR_ID_KEY, id);
      }
      return id;
    } catch {
      // localStorage unavailable (private mode / SSR): fall back to server-side IP+UA hash.
      return undefined;
    }
  }

  // Phase 1/2 — record an attributed outbound dealer click.
  public recordClick(request: ClickRequest): Observable<ClickResponse> {
    // The commerce surface is not on the production API; release hosts keep
    // the existing dealer tracking redirect only.
    if (!showUnfinished) return EMPTY;
    if (!request.visitorId) request.visitorId = this.getVisitorId();
    return this.http.post<ClickResponse>(`${this.base}/click`, request);
  }

  // Phase 4 — record an attributed lead.
  public recordLead(request: LeadRequest): Observable<{ leadId: number; billingStatus: string }> {
    return this.http.post<{ leadId: number; billingStatus: string }>(`${this.base}/lead`, request);
  }

  // Phase 3 — dealer-portal reporting (from attributed clicks).
  public getDealerMetrics(sourceId: number, days: number = 90): Observable<DealerDailyMetric[]> {
    return this.http.get<DealerDailyMetric[]>(`${this.base}/dealer/${sourceId}/metrics?days=${days}`);
  }

  public getTopPlates(sourceId: number, limit: number = 10): Observable<DealerTopPlate[]> {
    return this.http.get<DealerTopPlate[]>(`${this.base}/dealer/${sourceId}/top-plates?limit=${limit}`);
  }

  public getRecentActivity(sourceId: number, limit: number = 20): Observable<DealerRecentActivity[]> {
    return this.http.get<DealerRecentActivity[]>(`${this.base}/dealer/${sourceId}/activity?limit=${limit}`);
  }

  // Phase 5 — dealer invoices.
  public getInvoices(sourceId: number): Observable<CommerceInvoice[]> {
    return this.http.get<CommerceInvoice[]>(`${this.base}/dealer/${sourceId}/invoices`);
  }
}
